import { useEffect, useRef, type RefObject } from 'react'

const FOCUSABLE_SELECTOR =
  'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])'

/**
 * 底部 sheet 的基线焦点管理：
 * - 激活时把焦点移入容器内第一个可聚焦控件
 *   （带 data-sheet-initial-focus 的元素优先）；
 * - Tab / Shift+Tab 在容器内循环，焦点漂出时拉回；
 * - 传入 onClose 时 Escape 关闭 sheet；
 * - 关闭时若焦点无人接管（落在 body 或即将隐藏的容器内），恢复到触发元素；
 *   已被后续对话框接管时不抢焦点。
 */
export function useSheetFocus(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  onClose?: () => void,
  options?: {
    /** Escape 按下时先询问；返回 false 则本次不关闭（如容器内还有打开的弹出层）。 */
    shouldCloseOnEscape?: () => boolean
  },
) {
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const onCloseRef = useRef(onClose)
  const shouldCloseOnEscapeRef = useRef(options?.shouldCloseOnEscape)

  useEffect(() => {
    onCloseRef.current = onClose
    shouldCloseOnEscapeRef.current = options?.shouldCloseOnEscape
  })

  useEffect(() => {
    if (!active) {
      return undefined
    }

    returnFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    const container = ref.current
    // portal 到 body 的侧栏弹层不属于容器子树，但属于本层交互域，并入 Tab 序列。
    const focusableItems = () => {
      const ownItems = Array.from(
        container?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? [],
      )
      const popoverItems = Array.from(
        document.querySelectorAll<HTMLElement>('[data-sidebar-popover]'),
      ).flatMap((popover) => Array.from(popover.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)))

      return [...ownItems, ...popoverItems].filter((element) => !element.hasAttribute('disabled'))
    }

    const initialFocusTarget =
      container?.querySelector<HTMLElement>('[data-sheet-initial-focus]') ?? focusableItems()[0]
    // preventScroll:sheet 容器常位于带 transform 的祖先内,fixed 定位以该祖先为包含块,
    // 默认的焦点滚动会把祖先 scrollTop 推走,导致所有 sheet 整体漂移。
    initialFocusTarget.focus({ preventScroll: true })

    const handleKeyDown = (event: KeyboardEvent) => {
      // 存在更上层的模态（如结果图片预览）时，Escape 与 Tab 都归上层所有。
      // 「更上层」按 DOM 顺序判定：同层 modal 后挂载者在上；只比较其他 modal
      // 是否排在本容器之后，避免两个 modal 互相让位导致无人接管键盘。
      const modals = Array.from(document.querySelectorAll('[aria-modal="true"]'))
      const ownModal = container?.closest('[aria-modal="true"]') ?? container
      const ownIndex = ownModal ? modals.indexOf(ownModal) : -1
      const hasHigherModal = modals.some(
        (element, index) => index > ownIndex && !container?.contains(element),
      )

      if (hasHigherModal) {
        return
      }

      if (event.key === 'Escape') {
        if (shouldCloseOnEscapeRef.current?.() === false) {
          return
        }
        onCloseRef.current?.()
        return
      }

      if (event.key !== 'Tab') {
        return
      }

      const items = focusableItems()
      if (items.length === 0) {
        return
      }

      const first = items[0]
      const last = items[items.length - 1]
      const activeElement = document.activeElement
      // 弹层项已并入 items，焦点在容器内或 portal 弹层内都算「在域内」。
      const isInsideScope =
        activeElement instanceof HTMLElement &&
        (container?.contains(activeElement) === true || items.includes(activeElement))

      if (isInsideScope) {
        if (event.shiftKey && activeElement === first) {
          event.preventDefault()
          last.focus({ preventScroll: true })
        } else if (!event.shiftKey && activeElement === last) {
          event.preventDefault()
          first.focus({ preventScroll: true })
        }
        return
      }

      event.preventDefault()
      ;(event.shiftKey ? last : first).focus({ preventScroll: true })
    }

    document.addEventListener('keydown', handleKeyDown, true)

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      // 焦点无人接管（落在 body）或仍留在即将隐藏的容器里时才恢复；
      // 被后续对话框 autoFocus 接管时不抢。
      const activeElement = document.activeElement
      if (
        activeElement === null ||
        activeElement === document.body ||
        (activeElement instanceof HTMLElement && container?.contains(activeElement))
      ) {
        returnFocusRef.current?.focus({ preventScroll: true })
      }
      returnFocusRef.current = null
    }
  }, [active, ref])
}
